import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaArchive,
  FaCalendarAlt,
  FaChartLine,
  FaClipboardList,
  FaComments,
  FaLock,
  FaPlay,
  FaQuestionCircle,
  FaTools,
  FaVoteYea,
} from 'react-icons/fa';
import { usePaymentStatus } from '../../contexts/PaymentStatusContext';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import PrerenderReady from '../../components/PrerenderReady';
import './LiveAlgoLab.css';
const { getPublicPageSeo } = require('../../site/publicPageSeo');
const { structuredDataForRoute } = require('../../site/structuredData');

const LIVE_ALGO_LAB_STRUCTURED_DATA = structuredDataForRoute(getPublicPageSeo('/live-algo-lab'));

const imageBase = '/images/revenue';

// Rob, 2026-10-30: Tuesday Sessions with Rob stay with paid Pro and All
// Access ("keep it with Pro and All Access, just to keep it simple"). Access
// comes from Node's hasTuesdaySessionsAccess (paid Pro and All Access,
// Membership buyers, staff). Everyone else is offered Pro; the AI Trading
// Bootcamp Membership is a secondary option only.
const MEMBERSHIP_PATH = '/courses/ai-trading-bootcamp/self-paced';

const LiveAlgoLabSalesPage = () => {
  const { user } = useUser();
  const { hasTuesdaySessionsAccess } = usePaymentStatus();
  const { startCheckout, processing: isProcessing } = useCheckoutRedirect('live_algo_lab_sales');

  const handleJoin = useCallback(async () => {
    pushEvent({
      event: ANALYTICS_EVENTS.PRO_UPGRADE_CTA_CLICKED,
      source: window.location.pathname,
      ctaLocation: 'live_algo_lab_sales',
      hasTuesdaySessionsAccess,
    });

    if (hasTuesdaySessionsAccess) {
      window.location.assign('/live-algo-lab/watch');
      return;
    }

    await startCheckout({
      products: [{ productSlug: 'pro-plan' }],
      flowContext: 'live_algo_lab_sales',
      metadata: {
        origin: {
          flow: 'live_algo_lab_sales',
          page: window.location.pathname,
          returnUrl: '/live-algo-lab/watch',
        },
      },
      contact: {
        email: user?.email || '',
        name: user?.name || '',
      },
    });
  }, [hasTuesdaySessionsAccess, startCheckout, user?.email, user?.name]);

  return (
    <main className="lal-page">
      {/* Static sales copy: settled for the build-time crawler snapshot. */}
      <PrerenderReady ready />
      <Helmet>
        <title>Live Algo Lab | BotSpot</title>
        <meta
          name="description"
          content="Join Live Algo Lab every Tuesday with BotSpot Pro. Influence what gets built, get unstuck live, and watch the replay archive."
        />
        <link rel="canonical" href="https://botspot.trade/live-algo-lab" />
        {/* Route JSON-LD must survive mount; the static copy is removed with #root. */}
        {LIVE_ALGO_LAB_STRUCTURED_DATA.map((item, index) => (
          <script type="application/ld+json" key={`live-algo-lab-jsonld-${index}`}>
            {JSON.stringify(item)}
          </script>
        ))}
      </Helmet>

      <section className="lal-hero">
        <div className="lal-hero-copy">
          <p className="lal-kicker">Included with BotSpot Pro and All Access</p>
          <h1>Live Algo Lab</h1>
          <p className="lal-lede">
            Join Rob live every Tuesday, influence what gets built, and watch real BotSpot workflows
            come together.
          </p>
          <div className="lal-cta-row">
            <button
              type="button"
              className="lal-primary-btn"
              onClick={handleJoin}
              disabled={isProcessing}
            >
              <FaPlay />{' '}
              {hasTuesdaySessionsAccess
                ? 'Watch or join live'
                : isProcessing
                  ? 'Opening checkout...'
                  : 'Get Pro access'}
            </button>
            <a href="#how-live-algo-lab-works" className="lal-secondary-link">
              See how it works
            </a>
          </div>
        </div>
        <div className="lal-hero-media">
          <img
            src={`${imageBase}/live-algo-lab-hero.webp`}
            alt="Live Algo Lab session with voting, code, and trading chart"
            width="1024"
            height="572"
            loading="eager"
          />
          <p className="lal-media-caption">
            One live room: member input, implementation, and strategy output together.
          </p>
        </div>
      </section>

      <section className="lal-value-band">
        <div>
          <FaVoteYea />
          <span>Vote and influence builds</span>
        </div>
        <div>
          <FaComments />
          <span>Get help when stuck</span>
        </div>
        <div>
          <FaArchive />
          <span>Real replays as published</span>
        </div>
        <div>
          <FaChartLine />
          <span>Real bot workflows</span>
        </div>
      </section>

      <section className="lal-how-it-works" id="how-live-algo-lab-works">
        <div>
          <p className="lal-kicker">How the Lab Works</p>
          <h2>Every Tuesday is a working session, not a lecture.</h2>
          <p>
            Members bring ideas, blockers, and strategy questions. Rob uses the room to choose what
            to build, improve, or debug live. As real replays are published, they become part of the
            member archive.
          </p>
        </div>
        <div className="lal-step-grid">
          <article>
            <FaClipboardList />
            <h3>Bring ideas</h3>
            <p>Share the bot, signal, filter, agent workflow, or stuck point you want help with.</p>
          </article>
          <article>
            <FaVoteYea />
            <h3>Influence the build</h3>
            <p>The room votes and shapes what gets built, so sessions track real member demand.</p>
          </article>
          <article>
            <FaTools />
            <h3>Watch implementation</h3>
            <p>
              See BotSpot workflows, prompts, strategy logic, debugging, and backtest iteration
              happen live.
            </p>
          </article>
          <article>
            <FaQuestionCircle />
            <h3>Get unstuck</h3>
            <p>
              Use the live room for practical help when your own strategy or workflow hits a wall.
            </p>
          </article>
        </div>
      </section>

      <section className="lal-split">
        <figure className="lal-proof-figure">
          <img
            src={`${imageBase}/live-algo-lab-archive.webp`}
            alt="Live Algo Lab organized replay archive"
            width="1024"
            height="572"
            loading="eager"
          />
          <figcaption>Published sessions become a searchable working archive.</figcaption>
        </figure>
        <div>
          <p className="lal-kicker">Why People Join Live</p>
          <h2>It is not just a replay. It is live implementation time.</h2>
          <p>
            The best part of the Lab is the room. Members can bring ideas, vote on what gets built,
            ask questions, and get practical help when their own workflow gets stuck.
          </p>
          <ul className="lal-check-list">
            <li>Live build sessions every Tuesday unless the week is marked off.</li>
            <li>Public replays will be linked here once we have a real replay collection.</li>
            <li>
              Organized member archive as real recordings, summaries, and resources are published.
            </li>
            <li>Replays only appear when they point to a real published session.</li>
          </ul>
        </div>
      </section>

      <section className="lal-examples">
        <p className="lal-kicker">Example Session Themes</p>
        <h2>What we build changes with the room.</h2>
        <div className="lal-example-grid">
          <div>AI agent workflows that later become product features.</div>
          <div>Strategy filters, entry logic, risk rules, and backtest cleanup.</div>
          <div>Marketplace ideas, prompts, templates, and reusable bot components.</div>
          <div>Member support sessions where real blockers drive the agenda.</div>
        </div>
      </section>

      <section className="lal-final">
        <div>
          <p className="lal-kicker">
            <FaCalendarAlt /> Tuesdays
          </p>
          <h2>Use Pro to join the next session.</h2>
          <p>
            Starter users can upgrade when they want live help, live influence, and the organized
            archive.
          </p>
          {hasTuesdaySessionsAccess ? null : (
            <Link
              to={MEMBERSHIP_PATH}
              className="lal-secondary-link"
              data-testid="lal-membership-secondary"
            >
              Want every Bootcamp recording too? See the AI Trading Bootcamp Membership
            </Link>
          )}
        </div>
        <button
          type="button"
          className="lal-primary-btn"
          onClick={handleJoin}
          disabled={isProcessing}
        >
          {hasTuesdaySessionsAccess ? 'Open member page' : 'Upgrade to Pro'}
        </button>
      </section>

      <section className="lal-locked-preview">
        <FaLock />
        <span>
          Non-Pro visitors can see topics and titles, but live joining and the organized archive are
          Pro benefits.
        </span>
      </section>
    </main>
  );
};

export default LiveAlgoLabSalesPage;
